/**
 * Lightweight A/B test utility — no external dependencies.
 *
 * Variants are assigned once per browser (localStorage) and exposed on
 * window._abVariant so GA4 events can attach the variant label.
 *
 * Tests:
 *   cta_copy     — 'control'  = "Run My First Stress Test (30 sec)"
 *                  'variant'  = "See How Long My Super Lasts →"
 *
 *   presets_shown — 'control' = preset chips visible (current behaviour)
 *                   'variant' = preset chips hidden (user must fill own data)
 */

const STORAGE_KEY = 'ab_assignments';

const TESTS = {
  cta_copy:      ['control', 'variant'],
  presets_shown: ['control', 'variant'],
};

const loadAssignments = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveAssignments = (assignments) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(assignments));
  } catch {
    // storage unavailable — non-fatal
  }
};

export const initABTests = () => {
  const assignments = loadAssignments();
  let changed = false;

  Object.entries(TESTS).forEach(([testName, variants]) => {
    if (!assignments[testName]) {
      // 50/50 random assignment
      assignments[testName] = variants[Math.random() < 0.5 ? 0 : 1];
      changed = true;
    }
  });

  if (changed) saveAssignments(assignments);

  // Expose on window for GA4 event params
  window._abVariant = assignments.cta_copy;        // primary variant label for events
  window._abAssignments = assignments;

  // Fire assignment event once per session
  if (!sessionStorage.getItem('ab_assigned')) {
    sessionStorage.setItem('ab_assigned', '1');
    if (typeof gtag !== 'undefined') {
      gtag('event', 'ab_test_assigned', {
        event_category: 'experiment',
        cta_copy_variant: assignments.cta_copy,
        presets_shown_variant: assignments.presets_shown,
      });
    }
  }

  return assignments;
};

export const getVariant = (testName) => {
  if (window._abAssignments) return window._abAssignments[testName] || 'control';
  const assignments = loadAssignments();
  return assignments[testName] || 'control';
};
